import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { cn } from "../lib/cn";
import { useSlidingIndicator } from "../lib/use-sliding-indicator";

export interface SegmentedOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  options: SegmentedOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  size?: "sm" | "md";
  fullWidth?: boolean;
  className?: string;
  "aria-label": string;
}

/**
 * Contrôle segmenté : choix unique parmi 2 à 5 options courtes.
 *
 * Quand le préférer à un `Select` ou à un `RadioGroup` : lorsque toutes les
 * options tiennent sur une ligne et que les comparer d'un coup d'œil a du sens
 * (période, mode d'affichage, unité). Au-delà de cinq, la liste déroulante
 * redevient le bon choix.
 *
 * Sémantique `radiogroup` et non `tablist` : on choisit une valeur, on ne
 * navigue pas entre des panneaux. Les lecteurs d'écran annoncent alors
 * « 2 sur 3 », ce qui est l'information utile.
 */
export function SegmentedControl({
  options,
  value,
  defaultValue,
  onValueChange,
  size = "md",
  fullWidth,
  className,
  "aria-label": ariaLabel,
}: SegmentedControlProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const [internal, setInternal] = useState(defaultValue ?? options[0]?.value ?? "");

  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  const { rect, animated } = useSlidingIndicator(listRef, current, '[role="radio"][aria-checked="true"]');

  const select = (next: string) => {
    if (!isControlled) setInternal(next);
    onValueChange?.(next);
  };

  /** Dans un groupe radio, les flèches déplacent ET sélectionnent. */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;

    const usable = options.filter((option) => !option.disabled);
    const index = usable.findIndex((option) => option.value === current);
    if (index === -1) return;

    event.preventDefault();
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? usable.length - 1
          : event.key === "ArrowLeft"
            ? (index - 1 + usable.length) % usable.length
            : (index + 1) % usable.length;

    const target = usable[next];
    if (target) {
      select(target.value);
      listRef.current
        ?.querySelector<HTMLButtonElement>(`[data-value="${CSS.escape(target.value)}"]`)
        ?.focus();
    }
  };

  return (
    <div
      ref={listRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn(
        "relative inline-flex items-center gap-0.5 rounded-lg border border-border bg-surface-sunken p-1",
        "inset-shadow-control",
        fullWidth && "flex w-full",
        className,
      )}
    >
      {/* Pastille unique qui glisse : le déplacement continu relie l'ancienne
          sélection à la nouvelle, là où deux fondus font chercher l'œil.

          Le déplacement passe par `translate3d`, donc par le compositeur. La
          largeur, elle, est interpolée : une pastille bordée et arrondie
          supporte mal le `scaleX` qui écraserait ses rayons. `contain-layout`
          cantonne ce recalcul à un élément sans enfant — il ne peut pas
          déclencher de reflow du groupe. */}
      {rect && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-y-1 left-0 rounded-md border border-border/80 bg-surface-raised shadow-xs inset-shadow-raised",
            "transform-gpu contain-layout",
            animated && "transition-[transform,width] duration-slow ease-standard will-change-transform",
          )}
          style={{ transform: `translate3d(${rect.left}px, 0, 0)`, width: `${rect.width}px` }}
        />
      )}

      {options.map((option) => {
        const selected = option.value === current;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            data-value={option.value}
            aria-checked={selected}
            disabled={option.disabled}
            // Roving tabindex : un seul segment atteignable au Tab, les autres
            // aux flèches — convention des groupes radio.
            tabIndex={selected ? 0 : -1}
            onClick={() => select(option.value)}
            className={cn(
              "relative z-10 inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap touch-manipulation",
              "transform-gpu transition-[color,transform] duration-fast ease-standard active:scale-[0.98]",
              "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
              "disabled:pointer-events-none disabled:opacity-40",
              "[&_svg]:size-4 [&_svg]:shrink-0",
              size === "sm" ? "h-8 sm:h-7 px-2.5 text-xs" : "h-9 sm:h-8 px-3 text-sm",
              fullWidth && "flex-1",
              selected ? "text-fg" : "text-fg-muted hover:text-fg",
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
